import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, X } from 'lucide-react';
import BrandLogo from '../../components/common/BrandLogo';
import { toast } from 'sonner';
import { LANGUAGES, SPECIALIZATIONS, SERVICE_TYPES } from '../../lib/constants';

const MultiSelect = ({ options, selected, onChange, placeholder, testid }) => {
  const [input, setInput] = useState('');
  const add = (val) => {
    if (!val || selected.includes(val)) return;
    onChange([...selected, val]);
    setInput('');
  };
  const remove = (v) => onChange(selected.filter(s => s !== v));
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {selected.map(s => (
          <span key={s} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">
            {s}
            <button type="button" onClick={() => remove(s)} className="hover:text-red-600"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <select
        value=""
        onChange={(e) => add(e.target.value)}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
      >
        <option value="">{placeholder}</option>
        {options.filter(o => !selected.includes(o.value ?? o)).map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
        ))}
      </select>
    </div>
  );
};

const InterpreterRegister = () => {
  const { registerInterpreter } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    salutation: '', first_name: '', last_name: '', email: '', password: '',
    phone: '',
    street: '', house_number: '', postal_code: '', city: '', country: 'Deutschland',
    languages: [], target_languages: [],
    specializations: [], service_types: [], sworn: false
  });
  const [loading, setLoading] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.languages.length === 0 || form.target_languages.length === 0) {
      toast.error('Bitte mindestens eine Ausgangs- und Zielsprache auswählen');
      return;
    }
    setLoading(true);
    try {
      await registerInterpreter(form);
      toast.success('Registrierung erfolgreich. Ihr Konto wird vom Admin freigeschaltet.');
      navigate('/interpreter');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-2xl">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Zurück zur Anmeldung
        </Link>

        <div className="bg-white border border-slate-200 rounded-md p-8 card-subtle">
          <div className="mb-6">
            <BrandLogo height={40} />
          </div>

          <h1 className="font-heading text-2xl font-semibold text-slate-900 tracking-tight">Als Dolmetscher registrieren</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">Nach der Registrierung prüft unser Team Ihre Angaben und schaltet Ihr Konto frei.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="interpreter-register-form">
            <div className="grid grid-cols-[130px_1fr_1fr] gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Anrede</label>
                <select value={form.salutation} onChange={(e) => upd('salutation', e.target.value)} data-testid="reg-salutation-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  <option value="">—</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Vorname *</label>
                <input required value={form.first_name} onChange={(e) => upd('first_name', e.target.value)} data-testid="reg-firstname-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Nachname *</label>
                <input required value={form.last_name} onChange={(e) => upd('last_name', e.target.value)} data-testid="reg-lastname-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">E-Mail *</label>
                <input required type="email" value={form.email} onChange={(e) => upd('email', e.target.value)} data-testid="reg-email-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Telefon</label>
                <input value={form.phone} onChange={(e) => upd('phone', e.target.value)} data-testid="reg-phone-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Adresse (für Honorar-Abrechnung)</label>
              <div className="grid grid-cols-[1fr_100px] gap-2 mb-2">
                <input value={form.street} onChange={(e) => upd('street', e.target.value)} data-testid="reg-street-input" placeholder="Straße" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                <input value={form.house_number} onChange={(e) => upd('house_number', e.target.value)} data-testid="reg-housenr-input" placeholder="Hausnr." className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <input value={form.postal_code} onChange={(e) => upd('postal_code', e.target.value)} data-testid="reg-plz-input" placeholder="PLZ" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                <input value={form.city} onChange={(e) => upd('city', e.target.value)} data-testid="reg-city-input" placeholder="Ort" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Ausgangssprachen *</label>
                <MultiSelect options={LANGUAGES} selected={form.languages} onChange={(v) => upd('languages', v)} placeholder="Sprache wählen..." testid="reg-languages" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Zielsprachen *</label>
                <MultiSelect options={LANGUAGES} selected={form.target_languages} onChange={(v) => upd('target_languages', v)} placeholder="Sprache wählen..." testid="reg-target-languages" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Fachgebiete</label>
              <MultiSelect options={SPECIALIZATIONS} selected={form.specializations} onChange={(v) => upd('specializations', v)} placeholder="Fachgebiet wählen..." testid="reg-specializations" />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Einsatzarten</label>
              <MultiSelect options={SERVICE_TYPES} selected={form.service_types} onChange={(v) => upd('service_types', v)} placeholder="Einsatzart wählen..." testid="reg-service-types" />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
              <label className="inline-flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
                <input type="checkbox" checked={!!form.sworn} onChange={(e) => upd('sworn', e.target.checked)} data-testid="reg-sworn-input" className="w-4 h-4 mt-0.5" />
                <span>
                  <span className="font-medium">Ich bin allgemein beeidigt / vereidigt</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Nur ankreuzen, wenn Sie vor einem deutschen Gericht vereidigt sind. Nachweis wird im Onboarding geprüft.</span>
                </span>
              </label>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Passwort *</label>
              <input required type="password" minLength={6} value={form.password} onChange={(e) => upd('password', e.target.value)} data-testid="reg-password-input" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>

            <button type="submit" disabled={loading} data-testid="reg-submit-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2.5 text-sm transition-colors disabled:opacity-50">
              {loading ? 'Registrieren...' : 'Konto erstellen'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InterpreterRegister;
